import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Slider,
  Switch,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  IconButton,
  Tooltip,
  Alert
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  Help as HelpIcon,
  Code as CodeIcon
} from '@mui/icons-material';
import { DocumentFilters } from '@nvgc/shared';

interface AdvancedSearchProps {
  filters: DocumentFilters;
  onFiltersChange?: (filters: DocumentFilters) => void;
  onSearch?: (query: string, filters: DocumentFilters) => void;
}

interface AdvancedSearchOptions {
  exactPhrase: boolean;
  includeKeywords: string[];
  excludeKeywords: string[];
  minimumRelevanceScore: number;
  maxResults: number;
  searchInCitations: boolean;
  semanticSearch: boolean;
  fuzzyMatching: boolean;
}

const DEFAULT_ADVANCED_OPTIONS: AdvancedSearchOptions = {
  exactPhrase: false,
  includeKeywords: [],
  excludeKeywords: [],
  minimumRelevanceScore: 0.7,
  maxResults: 20,
  searchInCitations: true,
  semanticSearch: true,
  fuzzyMatching: false
};

export const AdvancedSearch: React.FC<AdvancedSearchProps> = ({
  filters,
  onSearch
}) => {
  const [query, setQuery] = useState('');
  const [advancedOptions, setAdvancedOptions] = useState<AdvancedSearchOptions>(DEFAULT_ADVANCED_OPTIONS);
  const [includeKeywordInput, setIncludeKeywordInput] = useState('');
  const [excludeKeywordInput, setExcludeKeywordInput] = useState('');
  const [showQueryBuilder, setShowQueryBuilder] = useState(false);

  const handleAdvancedOptionChange = <K extends keyof AdvancedSearchOptions>(
    key: K,
    value: AdvancedSearchOptions[K]
  ) => {
    setAdvancedOptions(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleAddKeyword = (type: 'include' | 'exclude') => {
    const input = type === 'include' ? includeKeywordInput : excludeKeywordInput;
    const setInput = type === 'include' ? setIncludeKeywordInput : setExcludeKeywordInput;
    
    if (!input.trim()) return;

    const keywords = input.split(',').map(k => k.trim()).filter(Boolean);
    
    setAdvancedOptions(prev => ({
      ...prev,
      [type === 'include' ? 'includeKeywords' : 'excludeKeywords']: [
        ...prev[type === 'include' ? 'includeKeywords' : 'excludeKeywords'],
        ...keywords
      ]
    }));
    
    setInput('');
  };

  const handleRemoveKeyword = (type: 'include' | 'exclude', keyword: string) => {
    setAdvancedOptions(prev => ({
      ...prev,
      [type === 'include' ? 'includeKeywords' : 'excludeKeywords']: 
        prev[type === 'include' ? 'includeKeywords' : 'excludeKeywords'].filter(k => k !== keyword)
    }));
  };

  const buildSearchQuery = () => {
    let builtQuery = query;

    // Add exact phrase quotes
    if (advancedOptions.exactPhrase && query && !query.includes('"')) {
      builtQuery = `"${query}"`;
    }

    // Add include keywords
    if (advancedOptions.includeKeywords.length > 0) {
      const includeTerms = advancedOptions.includeKeywords.map(k => `+${k}`).join(' ');
      builtQuery = `${builtQuery} ${includeTerms}`.trim();
    }

    // Add exclude keywords
    if (advancedOptions.excludeKeywords.length > 0) {
      const excludeTerms = advancedOptions.excludeKeywords.map(k => `-${k}`).join(' ');
      builtQuery = `${builtQuery} ${excludeTerms}`.trim();
    }

    return builtQuery;
  };

  const handleSearch = () => {
    const searchQuery = buildSearchQuery();
    onSearch?.(searchQuery, filters);
  };

  const handleClearAll = () => {
    setQuery('');
    setAdvancedOptions(DEFAULT_ADVANCED_OPTIONS);
    setIncludeKeywordInput('');
    setExcludeKeywordInput('');
  };

  const getQueryPreview = () => {
    return buildSearchQuery();
  };

  return (
    <Paper sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
        <SearchIcon color="primary" />
        <Typography variant="h6">Advanced Search</Typography>
        <Tooltip title="Advanced search allows you to use complex queries with boolean operators, exact phrases, and semantic search">
          <IconButton size="small">
            <HelpIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Main Search Query */}
      <Box sx={{ mb: 3 }}>
        <TextField
          fullWidth
          label="Search Query"
          placeholder="Enter your search terms..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          multiline
          rows={2}
          helperText="Use natural language or specific terms. Advanced options below will modify this query."
        />
      </Box>

      {/* Query Builder Toggle */}
      <Box sx={{ mb: 2 }}>
        <FormControlLabel
          control={
            <Switch
              checked={showQueryBuilder}
              onChange={(e) => setShowQueryBuilder(e.target.checked)}
            />
          }
          label="Show Query Builder"
        />
      </Box>

      {/* Query Builder */}
      {showQueryBuilder && (
        <Accordion sx={{ mb: 3 }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CodeIcon />
              <Typography>Query Builder</Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {/* Exact Phrase */}
              <FormControlLabel
                control={
                  <Checkbox
                    checked={advancedOptions.exactPhrase}
                    onChange={(e) => handleAdvancedOptionChange('exactPhrase', e.target.checked)}
                  />
                }
                label="Search for exact phrase"
              />

              {/* Include Keywords */}
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Must Include Keywords
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                  <TextField
                    size="small"
                    placeholder="Enter keywords (comma-separated)"
                    value={includeKeywordInput}
                    onChange={(e) => setIncludeKeywordInput(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleAddKeyword('include')}
                  />
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => handleAddKeyword('include')}
                  >
                    Add
                  </Button>
                </Box>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {advancedOptions.includeKeywords.map((keyword) => (
                    <Chip
                      key={keyword}
                      label={`+${keyword}`}
                      size="small"
                      color="success"
                      variant="outlined"
                      onDelete={() => handleRemoveKeyword('include', keyword)}
                    />
                  ))}
                </Box>
              </Box>

              {/* Exclude Keywords */}
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Must Exclude Keywords
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                  <TextField
                    size="small"
                    placeholder="Enter keywords to exclude (comma-separated)"
                    value={excludeKeywordInput}
                    onChange={(e) => setExcludeKeywordInput(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleAddKeyword('exclude')}
                  />
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => handleAddKeyword('exclude')}
                  >
                    Add
                  </Button>
                </Box>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {advancedOptions.excludeKeywords.map((keyword) => (
                    <Chip
                      key={keyword}
                      label={`-${keyword}`}
                      size="small"
                      color="error"
                      variant="outlined"
                      onDelete={() => handleRemoveKeyword('exclude', keyword)}
                    />
                  ))}
                </Box>
              </Box>

              {/* Query Preview */}
              {getQueryPreview() !== query && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Generated Query:
                  </Typography>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                    {getQueryPreview()}
                  </Typography>
                </Alert>
              )}
            </Box>
          </AccordionDetails>
        </Accordion>
      )}

      {/* Search Options */}
      <Accordion sx={{ mb: 3 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography>Search Options</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* Relevance Score */}
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Minimum Relevance Score: {advancedOptions.minimumRelevanceScore}
              </Typography>
              <Slider
                value={advancedOptions.minimumRelevanceScore}
                onChange={(_, value) => handleAdvancedOptionChange('minimumRelevanceScore', value as number)}
                min={0.1}
                max={1.0}
                step={0.1}
                marks={[
                  { value: 0.1, label: 'Low' },
                  { value: 0.5, label: 'Medium' },
                  { value: 0.9, label: 'High' }
                ]}
              />
              <Typography variant="caption" color="text.secondary">
                Higher scores return more relevant but fewer results
              </Typography>
            </Box>

            {/* Max Results */}
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Maximum Results: {advancedOptions.maxResults}
              </Typography>
              <Slider
                value={advancedOptions.maxResults}
                onChange={(_, value) => handleAdvancedOptionChange('maxResults', value as number)}
                min={5}
                max={100}
                step={5}
                marks={[
                  { value: 5, label: '5' },
                  { value: 20, label: '20' },
                  { value: 50, label: '50' },
                  { value: 100, label: '100' }
                ]}
              />
            </Box>

            {/* Search Options */}
            <FormGroup>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={advancedOptions.semanticSearch}
                    onChange={(e) => handleAdvancedOptionChange('semanticSearch', e.target.checked)}
                  />
                }
                label="Semantic Search (AI-powered understanding)"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={advancedOptions.searchInCitations}
                    onChange={(e) => handleAdvancedOptionChange('searchInCitations', e.target.checked)}
                  />
                }
                label="Include citations in search"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={advancedOptions.fuzzyMatching}
                    onChange={(e) => handleAdvancedOptionChange('fuzzyMatching', e.target.checked)}
                  />
                }
                label="Fuzzy matching (find similar spellings)"
              />
            </FormGroup>
          </Box>
        </AccordionDetails>
      </Accordion>

      {/* Action Buttons */}
      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
        <Button
          variant="outlined"
          startIcon={<ClearIcon />}
          onClick={handleClearAll}
        >
          Clear All
        </Button>
        <Button
          variant="contained"
          startIcon={<SearchIcon />}
          onClick={handleSearch}
          disabled={!query.trim()}
        >
          Advanced Search
        </Button>
      </Box>

      {/* Search Tips */}
      <Box sx={{ mt: 3, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
        <Typography variant="subtitle2" gutterBottom>
          Search Tips:
        </Typography>
        <Typography variant="body2" component="div">
          <ul style={{ margin: 0, paddingLeft: '1.2em' }}>
            <li>Use quotes for exact phrases: "gaming license"</li>
            <li>Use + to require terms: +casino +regulation</li>
            <li>Use - to exclude terms: casino -online</li>
            <li>Use wildcards: regulat* (finds regulation, regulations, etc.)</li>
            <li>Combine with filters for precise results</li>
          </ul>
        </Typography>
      </Box>
    </Paper>
  );
};